import * as React from 'react';
import Navbar from './nav/Navbar';
import Footer from '../main/nav/Footer';
import { IconButton } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import Swal from 'sweetalert2';
import { apiContext } from '../../Api';

const columns = [
    { id: 'id', label: 'No', width: '3%' },
    { id: 'dosen', label: 'Dosen / Peneliti', width: '15%' },
    { id: 'nip', label: 'NIP', width: '10%' },
    { id: 'judul', label: 'Title Publikasi', width: '34%' },
    { id: 'tipe', label: 'Jenis Publikasi', width: '12%' },
    { id: 'tahun', label: 'Tahun', width: '6%' },
    { id: 'file', label: 'File', width: '5%' },
    { id: 'status', label: 'Status', width: '8%' },
    { id: 'option', label: 'Verifikasi', width: '7%' },
];

export default function DashboardReview() {
    const { axios, API_URL } = React.useContext(apiContext);
  
    const [userData, setUserData] = React.useState([]);
    const [searchVal, setSearchVal] = React.useState('');
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);

    const fetchUser = async () => {
        const { data } = await axios.get(`/reviewer/data`);
        if (data) {
            setUserData(data);
        }
    };

    React.useEffect(() => {
        fetchUser();
    }, []);

    const getStatusLabel = (status) => {
        switch (status) {
            case 1:
                return <span className="inline-flex rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-extrabold text-brand-green">Diterima</span>;
            case 2:
                return <span className="inline-flex rounded-full bg-amber-50 px-2.5 py-1 text-xs font-extrabold text-yellow-600">Revisi</span>;
            case 3:
                return <span className="inline-flex rounded-full bg-rose-50 px-2.5 py-1 text-xs font-extrabold text-red-600">Ditolak</span>;
            default:
                return <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-extrabold text-slate-500">Draft</span>;
        }
    };

    const changeStatus = async (publicationId, newStatus) => {
        Swal.fire({
            title: 'Apakah Anda yakin?',
            text: `Mengubah status publikasi menjadi ${newStatus === 1 ? 'Diterima' : 'Ditolak'}`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ya',
            cancelButtonText: 'Batal'
        }).then((result) => {
            if (result.isConfirmed) {
                submitStatus(publicationId, newStatus, '');
            }
        });
    };

    const submitStatus = async (publicationId, status, komentar) => {
        try {
            Swal.fire({
                title: 'Mohon tunggu',
                text: 'Sedang memperbarui status publikasi.',
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            await axios.post('/reviewer/verifikasi', {
                publikasi_id: publicationId,
                status: status,
                komentar: komentar
            });

            Swal.fire({
                icon: 'success',
                title: 'Berhasil',
                text: 'Status publikasi berhasil diperbarui.',
                timer: 1500,
                showConfirmButton: false
            });

            fetchUser();
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Gagal',
                text: error?.response?.data?.message || 'Gagal memperbarui status.'
            });
        }
    };

    const edit = async (e, id) => {
        Swal.fire({
            title: 'Apakah Anda yakin?',
            text: 'Membatalkan verifikasi akan mereset status kembali ke Draft.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Ya',
            cancelButtonText: 'Batal'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`/reviewer/verifikasi-delete/${id}`);
                    Swal.fire({
                        icon: 'success',
                        title: 'Berhasil',
                        text: 'Verifikasi berhasil dibatalkan.',
                        timer: 1500,
                        showConfirmButton: false
                    });
                    fetchUser();
                } catch (error) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Gagal',
                        text: error?.response?.data?.message || 'Gagal membatalkan verifikasi.'
                    });
                }
            }
        });
    };

    const filteredData = React.useMemo(() => {
        if (!searchVal) return userData;
        const q = searchVal.toLowerCase();
        return userData.filter((item) => {
            return (
                (item.nama_dosen && item.nama_dosen.toLowerCase().includes(q)) ||
                (item.nip_dosen && item.nip_dosen.toLowerCase().includes(q)) ||
                (item.judul && item.judul.toLowerCase().includes(q)) ||
                (item.tipe && item.tipe.toLowerCase().includes(q)) ||
                (item.tahun && String(item.tahun).includes(q))
            );
        });
    }, [userData, searchVal]);

    const safePage = Math.max(0, Math.min(page, Math.ceil(filteredData.length / rowsPerPage) - 1));
    const visibleRows = filteredData.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);
    const pageCount = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

    return (
        <div className="min-h-screen bg-brand-soft text-brand-navy md:flex">
            <Navbar title="Review Publikasi" />

            <main className="min-w-0 flex-1 pt-20 md:pt-24 mt-3">
                <div className="mx-auto max-w-7xl px-4 pb-10 sm:px-6 lg:px-8">
                    <section className="relative overflow-hidden rounded-2xl bg-white shadow-soft p-6 border border-slate-200">
                        <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-transparent via-brand-gold to-transparent opacity-90"></div>
                        <div className="flex flex-col">
                            <h2 className="font-display text-xl font-extrabold text-brand-navy">
                                Data Publikasi Peneliti
                            </h2>
                            <p className="mt-1 text-sm text-slate-500 mb-6">
                                Daftar berkas publikasi dosen/peneliti yang diajukan melalui RPU untuk divalidasi statusnya.
                            </p>
                            
                            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="relative max-w-md w-full">
                                    <input
                                        type="text"
                                        placeholder="Cari Dosen, Judul, atau NIP..."
                                        value={searchVal}
                                        onChange={(e) => {
                                            setSearchVal(e.target.value);
                                            setPage(0);
                                        }}
                                        className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-10 pr-4 text-sm font-semibold outline-none transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                    />
                                    <div className="absolute left-3 top-3 text-slate-400">
                                        <svg
                                            aria-hidden="true"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="h-4 w-4"
                                        >
                                            <path d="m21 21-4.3-4.3M10.8 18a7.2 7.2 0 1 1 0-14.4 7.2 7.2 0 0 1 0 14.4Z" />
                                        </svg>
                                    </div>
                                </div>
                                
                                <div className="flex items-center gap-3 text-sm text-slate-600">
                                    <span>
                                        Menampilkan <strong className="text-brand-navy">{visibleRows.length}</strong> dari <strong className="text-brand-navy">{filteredData.length}</strong> data
                                    </span>
                                    <label className="inline-flex items-center gap-2 font-semibold">
                                        Baris
                                        <select
                                            value={rowsPerPage}
                                            onChange={(event) => {
                                                setRowsPerPage(Number(event.target.value));
                                                setPage(0);
                                            }}
                                            className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15"
                                        >
                                            {[20, 50, 100].map((item) => (
                                                <option key={item} value={item}>{item}</option>
                                            ))}
                                        </select>
                                    </label>
                                </div>
                            </div>

                            <div className="mt-4 overflow-hidden rounded-lg border border-slate-200">
                                <div className="overflow-x-auto">
                                    <table className="min-w-[1200px] w-full divide-y divide-slate-200 text-left text-sm">
                                        <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                                            <tr>
                                                {columns.map((column) => (
                                                    <th key={column.id} scope="col" className="px-4 py-3 font-extrabold" style={{ width: column.width }}>
                                                        {column.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 bg-white">
                                            {visibleRows.length ? visibleRows.map((row, index) => {
                                                const rowNum = safePage * rowsPerPage + index + 1;
                                                return (
                                                    <tr key={row.id} className="align-top transition hover:bg-brand-soft/70">
                                                        <td className="px-4 py-4 font-bold text-slate-500">
                                                            {rowNum}
                                                        </td>
                                                        <td className="px-4 py-4 font-bold text-brand-navy">
                                                            {row.nama_dosen}
                                                        </td>
                                                        <td className="px-4 py-4 text-slate-600 font-semibold">
                                                            {row.nip_dosen}
                                                        </td>
                                                        <td className="max-w-xs px-4 py-4 font-bold text-brand-navy whitespace-normal break-words">
                                                            {row.judul || '-'}
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            <span className="inline-flex rounded-full bg-brand-soft px-3 py-1 text-xs font-extrabold text-brand-green">
                                                                {row.tipe || '-'}
                                                            </span>
                                                        </td>
                                                        <td className="px-4 py-4 font-semibold text-slate-600">
                                                            {row.tahun || '-'}
                                                        </td>
                                                        <td className="px-4 py-4 text-center">
                                                            {row.file ? (
                                                                <a target="_blank" href={API_URL + '/storage/' + row.file} rel="noreferrer">
                                                                    <PictureAsPdfIcon color="error" />
                                                                </a>
                                                            ) : (
                                                                <span className="text-xs font-semibold text-slate-400">TIDAK ADA</span>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            {getStatusLabel(row.status)}
                                                            {row.status === 2 && row.komentar && (
                                                                <div className="text-[10px] text-slate-500 mt-1 max-w-xs break-words italic leading-normal">
                                                                    Catatan: {row.komentar}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-4 py-4">
                                                            {row.asal_data !== 'RPU' ? (
                                                                <span className="text-xs font-semibold text-slate-400 italic">Otomatis Diterima</span>
                                                            ) : row.status !== 0 ? (
                                                                <div className="flex items-center gap-1">
                                                                    {row.status === 1 && <CheckIcon color="success" />}
                                                                    {row.status === 3 && <CloseIcon color="error" />}
                                                                    <IconButton onClick={(e) => edit(e, row.id)} size="small" className="hover:bg-slate-100">
                                                                        <EditIcon fontSize="small" />
                                                                    </IconButton>
                                                                </div>
                                                            ) : (
                                                                <div className="flex items-center gap-1">
                                                                    <IconButton title="Diterima" onClick={() => changeStatus(row.id, 1)} size="small" className="hover:bg-emerald-50 text-emerald-600">
                                                                        <CheckIcon color="success" />
                                                                    </IconButton>
                                                                    <IconButton title="Ditolak" onClick={() => changeStatus(row.id, 3)} size="small" className="hover:bg-rose-50 text-rose-600">
                                                                        <CloseIcon color="error" />
                                                                    </IconButton>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            }) : (
                                                <tr>
                                                    <td colSpan={columns.length} className="px-4 py-12 text-center text-sm font-semibold text-slate-500">
                                                        Data tidak ditemukan.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-slate-600">
                                <span className="text-sm font-semibold text-slate-500">
                                    Halaman {safePage + 1} dari {pageCount}
                                </span>
                                <div className="inline-flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setPage((value) => Math.max(0, value - 1))}
                                        disabled={safePage === 0}
                                        className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-brand-navy transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Prev
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPage((value) => Math.min(pageCount - 1, value + 1))}
                                        disabled={safePage >= pageCount - 1}
                                        className="inline-flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-bold text-brand-navy transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>

                    <div className="mt-8 rounded-lg bg-brand-navy text-white">
                        <Footer />
                    </div>
                </div>
            </main>
        </div>
    );
}
